import { mapActions } from 'vuex';

import { api } from '_/services/api';

// Polling interval. 60s for the analytics dashboard: the numbers come
// from grouped queries over `users`, `predictions` and `articles` — all
// cheap, but no need to recompute more often than the editor would
// glance at the page. Pipeline-monitor polls at 30s because it's
// driving live triage; this dashboard is for trend-watching.
const POLL_INTERVAL_MS = 60000;


function formatRelative (iso) {
    if (!iso) return '—';
    const t = new Date(iso).getTime();
    const dt = Date.now() - t;
    if (Number.isNaN(t)) return '—';
    if (dt < 60_000)       return Math.floor(dt / 1000) + 's ago';
    if (dt < 3_600_000)    return Math.floor(dt / 60_000) + 'm ago';
    if (dt < 86_400_000)   return Math.floor(dt / 3_600_000) + 'h ago';
    return Math.floor(dt / 86_400_000) + 'd ago';
}


function formatNumber (n) {
    if (n === null || n === undefined) return '—';
    if (typeof n !== 'number') return String(n);
    return n.toLocaleString('en-US');
}


function formatPercent (frac, digits = 1) {
    if (frac === null || frac === undefined) return '—';
    return (frac * 100).toFixed(digits) + '%';
}


export default {
    name: 'AnalyticsDashboard',

    data () {
        return {
            metrics: null,
            snapshotAt: null,
            loading: false,
            error: null,
            _pollHandle: null,
        };
    },

    computed: {
        breadcrumbItems () {
            return [{
                text: 'Analytics',
                link: 'analytics',
            }];
        },

        // Active predictors / total registered — the "% of users who
        // engage with predictions" headline ratio. Capped at the
        // denominator so we don't display 200% when test predictors
        // outlive their user accounts.
        engagementRate7d () {
            if (!this.metrics) return null;
            const total = this.metrics.registered_users.total;
            const active = this.metrics.active_predictors.active_7d;
            if (!total) return null;
            return Math.min(active / total, 1);
        },

        resolutionTotal () {
            if (!this.metrics) return 0;
            const r = this.metrics.predictions.resolution;
            return (r.correct || 0) + (r.incorrect || 0);
        },
    },

    methods: {
        ...mapActions('ui', [
            'animateWrapper',
            'setBodyClass',
            'setBreadcrumbItems',
        ]),

        formatRelative,
        formatNumber,
        formatPercent,

        async refresh () {
            this.loading = true;
            try {
                const response = await api.get('admin/analytics/key-metrics');
                // PCMS double-wraps standard controller responses: the
                // outer wrapper is `{message, body: …}`, and the inner
                // body is itself `{message, body: {…actual payload…}}`.
                // Pipeline-monitor avoids this because its proxy
                // forwards raw FastAPI; standard PCMS controllers do
                // not. Unwrap both layers, falling back to a single
                // unwrap or the raw response for any other shape.
                const outer = response && response.body ? response.body : response;
                const payload = outer && outer.body ? outer.body : outer;
                this.metrics = payload;
                this.snapshotAt = payload.as_of;
                this.error = null;
            } catch (exc) {
                this.error = (exc && exc.message)
                    || (exc && exc.response && exc.response.data && exc.response.data.message)
                    || 'Could not load analytics metrics.';
            } finally {
                this.loading = false;
            }
        },

        startPolling () {
            this.refresh();
            this._pollHandle = setInterval(() => this.refresh(), POLL_INTERVAL_MS);
        },

        stopPolling () {
            if (this._pollHandle) {
                clearInterval(this._pollHandle);
                this._pollHandle = null;
            }
        },
    },

    mounted () {
        this.$nextTick(function () {
            this.setBodyClass('admin');
            this.setBreadcrumbItems(this.breadcrumbItems);
            this.animateWrapper();
            this.startPolling();
        });
    },

    beforeDestroy () {
        this.stopPolling();
    },
};
